import SectionHeading from "./SectionHeading";

const entries = [
  {
    icon: "ri-list-check-2",
    title: "题材细分",
    href: "#genres",
    desc: "家庭、婚姻、禁忌、成长、都市与罪罚六个方向",
  },
  {
    icon: "ri-bar-chart-grouped-line",
    title: "分级尺度",
    href: "#rating",
    desc: "从 G 到 NC-17，理解「尺度」到底指什么",
  },
  {
    icon: "ri-film-line",
    title: "代表作品",
    href: "#works",
    desc: "十部作品的主题与影评要点逐一展开",
  },
  {
    icon: "ri-question-line",
    title: "常见问题",
    href: "#faq",
    desc: "未删减、大尺度、如何选择一次讲清",
  },
];

export default function IntroSection() {
  return (
    <section id="intro" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-24">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <SectionHeading
              eyebrow="What it is"
              title="先弄清楚：欧美伦理电影到底指什么"
            />
            <div className="mt-6 space-y-4 text-sm md:text-base leading-relaxed text-foreground-700">
              <p>
                严格来说，「伦理电影」并不是一条清晰的类型边界，而更像一个以
                <strong className="text-foreground-950 font-semibold">道德处境与亲密关系</strong>
                为核心的内容方向。它通常不依赖动作与奇观，而是把镜头留在客厅、卧室与餐桌，让人物在关系里互相试探、误解与和解。
              </p>
              <p>
                在欧美的创作传统中，它常与剧情片、独立电影、戏剧改编互相交叠：欧洲一侧更偏作者表达与留白，美国一侧更贴近中产生活与叙事推进。
                所谓
                <strong className="text-foreground-950 font-semibold">在线、未删减、禁忌、人性、尺度</strong>
                这些搜索方向，其实都是在从不同侧面靠近同一个问题——作品如何谈论那些我们平时不愿说出口的事。
              </p>
              <p>
                本站面向对人性议题与文艺剧情片感兴趣的成年观众：不猎奇、不堆砌片单，而是先把概念、题材、分级与版本讲清楚，帮你建立自己的判断标准。
              </p>
            </div>

            <div className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-primary-600 text-lg"></i>
                <span className="font-label text-sm text-foreground-700">强调人物与主题，而非感官刺激</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-compass-3-line text-primary-600 text-lg"></i>
                <span className="font-label text-sm text-foreground-700">给出判断框架，而非标准答案</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="w-full aspect-[4/5] md:aspect-[5/6] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20softly%20lit%20vintage%20film%20projector%20casting%20a%20warm%20light%20beam%20in%20a%20dark%20room%2C%20floating%20dust%20particles%2C%20moody%20editorial%20atmosphere%2C%20muted%20warm%20amber%20and%20deep%20brown%20palette&width=900&height=1080&seq=intro-projector&orientation=portrait"
                alt="老式电影放映机与暖色光束，象征伦理片的叙事氛围"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-3 md:left-6 bg-primary-500 text-background-50 rounded-lg px-5 py-4 max-w-[16rem]">
              <div className="font-heading text-lg font-semibold">一句话概括</div>
              <p className="mt-1 text-xs leading-relaxed text-background-50/90">
                伦理片问的从来不是「谁对」，而是「换作是你，会怎么做」。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-secondary-500" />
            <span className="font-label text-xs md:text-sm tracking-[0.28em] text-secondary-700 uppercase">
              Quick access
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {entries.map((e) => (
              <a
                key={e.title}
                href={e.href}
                className="group bg-background-100 rounded-lg p-5 border border-background-200/70 hover:bg-secondary-50 hover:border-secondary-200 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 group-hover:bg-secondary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${e.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base md:text-lg font-semibold text-foreground-950">
                  {e.title}
                </h3>
                <p className="mt-1.5 text-xs md:text-sm leading-relaxed text-foreground-600">{e.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 font-label text-xs text-secondary-700 group-hover:gap-2 transition-all">
                  前往查看
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}